import { useEffect } from 'react';

import { TFunction } from 'i18next';

import { Button } from '@/components/ui/button';

import { SettingsFooterState } from '../../types/settings-footer';

interface UseMemberAccessFooterParams {
  /** Members whose group differs from the server baseline (normal-mode count). */
  changesCount: number;
  /** Currently-selected members across all filters (invite-mode count). */
  selectedCount: number;
  isInviteMode: boolean;
  isLoading: boolean;
  error: Error | null;
  isPending: boolean;
  isInvitePending: boolean;
  onSave: () => void;
  onInviteAndSave: () => void;
  onCancelInviteMode: () => void;
  onReset: () => void;
  setFooterState: (state: SettingsFooterState) => void;
  t: TFunction;
}

export const useMemberAccessFooter = ({
  changesCount,
  selectedCount,
  isInviteMode,
  isLoading,
  error,
  isPending,
  isInvitePending,
  onSave,
  onInviteAndSave,
  onCancelInviteMode,
  onReset,
  setFooterState,
  t,
}: UseMemberAccessFooterParams) => {
  useEffect(() => {
    if (error || isLoading) {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
      return;
    }

    if (isInviteMode) {
      const cancelButton = (
        <Button type="button" variant="outline" onClick={onCancelInviteMode}>
          {t('pages.members.cancelInviteMode')}
        </Button>
      );

      setFooterState({
        isVisible: true,
        isEnabled: selectedCount > 0,
        isLoading: isPending || isInvitePending,
        onSave: onInviteAndSave,
        saveLabel: t('pages.members.inviteAndSave'),
        savingLabel: t('pages.members.inviting'),
        // Cancel sits on the LEFT (inside leftAction), to the right of the count span.
        leftAction:
          selectedCount > 0 ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {t('pages.members.membersSelected', { count: selectedCount })}
              </span>
              {cancelButton}
            </div>
          ) : (
            cancelButton
          ),
        disabledReason: selectedCount === 0 ? t('pages.members.noMembersSelected') : undefined,
      });
      return;
    }

    setFooterState({
      isVisible: true,
      isEnabled: changesCount > 0,
      isLoading: isPending,
      onSave,
      leftAction:
        changesCount > 0 ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {t('pages.members.changesCount', { count: changesCount })}
            </span>
            <Button type="button" variant="outline" onClick={onReset}>
              {t('pages.members.resetValues')}
            </Button>
          </div>
        ) : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading, isInviteMode, changesCount, selectedCount, isPending, isInvitePending]);

  useEffect(() => {
    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [setFooterState]);
};
